import { useState } from 'react';
import { useConfirm } from '../../context/ConfirmContext';
import {
  useBewerberTimeline,
  useCreateActivity,
  useDeleteActivity,
  useBedarfTimeline,
  useCreateBedarfActivity,
  useDeleteBedarfActivity,
} from '../../api/activities';
import { ActivityTyp, ActivityTypConfig, CreateActivityDto } from '../../types/activity';
import { TimelineItem } from './TimelineItem';
import { ActivityForm } from './ActivityForm';

interface ActivityTimelineProps {
  bewerberId?: string;
  bedarfId?: string;
}

export function ActivityTimeline({ bewerberId, bedarfId }: ActivityTimelineProps) {
  const confirm = useConfirm();
  const [showForm, setShowForm] = useState(false);
  const [selectedType, setSelectedType] = useState<ActivityTyp | null>(null);
  const [page, setPage] = useState(1);
  const limit = 20;

  // Determine which entity we're working with
  const isBewerber = !!bewerberId;

  // Use the appropriate hooks based on entity type
  const bewerberTimeline = useBewerberTimeline(bewerberId || '', { page, limit });
  const bedarfTimeline = useBedarfTimeline(bedarfId || '', { page, limit });

  const createBewerberActivity = useCreateActivity();
  const createBedarfActivityMutation = useCreateBedarfActivity();

  const deleteBewerberActivity = useDeleteActivity();
  const deleteBedarfActivityMutation = useDeleteBedarfActivity();

  // Select the correct data based on entity type
  const { data, isLoading, error } = isBewerber ? bewerberTimeline : bedarfTimeline;
  const createActivityMutation = isBewerber ? createBewerberActivity : createBedarfActivityMutation;

  const handleCreateActivity = (formData: CreateActivityDto) => {
    if (isBewerber && bewerberId) {
      createBewerberActivity.mutate(
        { bewerberId, data: formData },
        {
          onSuccess: () => {
            setShowForm(false);
            setSelectedType(null);
          },
        },
      );
    } else if (bedarfId) {
      createBedarfActivityMutation.mutate(
        { bedarfId, data: formData },
        {
          onSuccess: () => {
            setShowForm(false);
            setSelectedType(null);
          },
        },
      );
    }
  };

  const handleDeleteActivity = async (activityId: string) => {
    const confirmed = await confirm({
      title: 'Aktivität löschen',
      message: 'Möchten Sie diese Aktivität wirklich löschen?',
      confirmLabel: 'Löschen',
      variant: 'danger',
    });
    if (confirmed) {
      if (isBewerber && bewerberId) {
        deleteBewerberActivity.mutate({ bewerberId, activityId });
      } else if (bedarfId) {
        deleteBedarfActivityMutation.mutate({ bedarfId, activityId });
      }
    }
  };

  const handleQuickAdd = (typ: ActivityTyp) => {
    setSelectedType(typ);
    setShowForm(true);
  };

  if (error) {
    return (
      <div className="text-red-600 p-4">
        Fehler beim Laden der Timeline: {(error as Error).message}
      </div>
    );
  }

  return (
    <div className="bg-white shadow rounded-lg">
      {/* Header */}
      <div className="px-4 py-5 sm:px-6 border-b border-gray-200">
        <h3 className="text-lg font-medium leading-6 text-gray-900">Aktivitäten</h3>
        <p className="mt-1 text-sm text-gray-500">
          Notizen, Anrufe, E-Mails und Meetings
        </p>
      </div>

      {/* Quick Add Buttons */}
      <div className="px-4 py-3 sm:px-6 border-b border-gray-200 bg-gray-50">
        <div className="flex flex-wrap gap-2">
          {Object.entries(ActivityTypConfig).map(([key, config]) => (
            <button
              key={key}
              onClick={() => handleQuickAdd(key as ActivityTyp)}
              className={`inline-flex items-center px-3 py-1.5 rounded-md text-sm font-medium ${config.bgColor} ${config.color} hover:opacity-80 transition-opacity`}
            >
              <span className="mr-1">+</span>
              {config.label}
            </button>
          ))}
        </div>
      </div>

      {/* Activity Form Modal */}
      {showForm && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex min-h-full items-end justify-center p-4 text-center sm:items-center sm:p-0">
            <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" onClick={() => setShowForm(false)} />
            <div className="relative transform overflow-hidden rounded-lg bg-white px-4 pb-4 pt-5 text-left shadow-xl transition-all sm:my-8 sm:w-full sm:max-w-lg sm:p-6">
              <div className="mb-4">
                <h3 className="text-lg font-medium leading-6 text-gray-900">
                  Neue Aktivität
                </h3>
              </div>
              <ActivityForm
                initialData={selectedType ? { typ: selectedType } : undefined}
                onSubmit={handleCreateActivity}
                onCancel={() => {
                  setShowForm(false);
                  setSelectedType(null);
                }}
                isLoading={createActivityMutation.isPending}
              />
            </div>
          </div>
        </div>
      )}

      {/* Timeline Content */}
      <div className="px-4 py-5 sm:px-6">
        {isLoading ? (
          <div className="flex justify-center py-8">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600" />
          </div>
        ) : !data?.data.length ? (
          <div className="text-center py-8 text-gray-500">
            <p>Noch keine Aktivitäten vorhanden.</p>
            <p className="text-sm mt-1">Erstellen Sie Ihre erste Aktivität über die Buttons oben.</p>
          </div>
        ) : (
          <div className="space-y-6">
            {data.data.map((event) => (
              <TimelineItem
                key={event.id}
                event={event}
                onDelete={event.type === 'activity' ? handleDeleteActivity : undefined}
              />
            ))}
          </div>
        )}
      </div>

      {/* Pagination */}
      {data?.meta && data.meta.totalPages > 1 && (
        <div className="px-4 py-3 sm:px-6 border-t border-gray-200 flex items-center justify-between">
          <div className="text-sm text-gray-500">
            Seite {data.meta.page} von {data.meta.totalPages} ({data.meta.total} Einträge)
          </div>
          <div className="flex gap-2">
            <button
              onClick={() => setPage((p) => Math.max(1, p - 1))}
              disabled={page === 1}
              className="px-3 py-1 text-sm border rounded-md disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50"
            >
              Zurück
            </button>
            <button
              onClick={() => setPage((p) => p + 1)}
              disabled={page >= data.meta.totalPages}
              className="px-3 py-1 text-sm border rounded-md disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50"
            >
              Weiter
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
